import {
  deleteReferenceListingById,
  getReferenceListings,
  putReferenceListing,
} from '@/api/referenceListings';
import { Currency } from '@/types';
import { AgencyAgentType, ApiCallStatus, ReferenceListing } from '@/types-reference-listing';
import {
  Address,
  Attachment,
  Category,
  Characteristics,
  Listing,
  OfferType,
} from '@smg-real-estate/hg-rets-schema';
import { toRaw } from 'vue';
import { ActionContext } from 'vuex';

export interface ReferenceListingsState {
  draftListing: ReferenceListing;
  listings: ReferenceListing[];
  apiGetCallStatus: ApiCallStatus;
  apiDeleteCallStatus?: ApiCallStatus;
  apiPutCallStatus: ApiCallStatus;
}

const getInitialState = (): ReferenceListingsState => ({
  draftListing: {
    offerType: OfferType.Buy,
    categories: [],
    address: {
      postalCode: '',
      locality: undefined,
    },
    characteristics: {
      numberOfRooms: undefined,
    },
    localization: {
      de: {
        text: {
          description: '',
          title: '',
        },
        attachments: [],
        urls: [],
      },
      primary: 'de',
    },
    prices: {
      currency: Currency.CHF,
      buy: {
        price: undefined,
      },
    },
  },
  listings: [],
  apiGetCallStatus: ApiCallStatus.READY,
  apiPutCallStatus: ApiCallStatus.READY,
});

const mutations = {
  setAttachments(state: ReferenceListingsState, value: Attachment[]): void {
    if (state.draftListing.localization?.de) {
      state.draftListing.localization.de.attachments = value;
    }
  },
  updateDraftListing(state: ReferenceListingsState, updates: ReferenceListing): void {
    const clonedUpdates = structuredClone(toRaw(updates));
    state.draftListing = clonedUpdates;

    // Transform Commercial listings: map totalFloorSpace to livingSpace for form display
    if (!clonedUpdates.categories?.includes(Category.Commercial)) {
      return;
    }

    if (clonedUpdates.characteristics?.totalFloorSpace != null) {
      // Map totalFloorSpace to livingSpace for form display
      const totalFloorSpaceValue = clonedUpdates.characteristics.totalFloorSpace;
      const { totalFloorSpace, ...characteristicsWithoutTotalFloorSpace } =
        clonedUpdates.characteristics;
      state.draftListing.characteristics = {
        ...characteristicsWithoutTotalFloorSpace,
        livingSpace: totalFloorSpaceValue,
      };
    }
  },
  setCategories(state: ReferenceListingsState, categories: Category[]): void {
    state.draftListing.categories = categories;
  },
  resetDraftListing(state: ReferenceListingsState): void {
    state.draftListing = getInitialState().draftListing;
  },
  setNumberOfRooms(state: ReferenceListingsState, numberOfRooms: number): void {
    (state.draftListing.characteristics as Characteristics).numberOfRooms = numberOfRooms;
  },
  setLivingSpace(state: ReferenceListingsState, livingSpace: number): void {
    (state.draftListing.characteristics as Characteristics).livingSpace = livingSpace;
  },
  setPrice(state: ReferenceListingsState, price: number): void {
    if (state.draftListing.prices?.buy) {
      state.draftListing.prices.buy.price = price;
    }
  },
  setAgent(state: ReferenceListingsState, agent: AgencyAgentType): void {
    state.draftListing.agencyAgent = agent;
  },
  setListingZipCode(state: ReferenceListingsState, zipCode: string): void {
    (state.draftListing.address as Address).postalCode = zipCode;
  },
  setListingCity(state: ReferenceListingsState, city: string): void {
    (state.draftListing.address as Address).locality = city;
  },
  setListings: (state: ReferenceListingsState, listings: ReferenceListing[]): void => {
    state.listings = listings;
  },
  setApiGetCallStatus: (state: ReferenceListingsState, apiGetCallStatus: ApiCallStatus): void => {
    state.apiGetCallStatus = apiGetCallStatus;
  },
  setApiDeleteCallStatus: (
    state: ReferenceListingsState,
    apiDeleteCallStatus: ApiCallStatus,
  ): void => {
    state.apiDeleteCallStatus = apiDeleteCallStatus;
  },
  setApiPutCallStatus: (state: ReferenceListingsState, apiPutCallStatus: ApiCallStatus): void => {
    state.apiPutCallStatus = apiPutCallStatus;
  },
};

const getters = {
  getListingAttachments: (state: ReferenceListingsState): Attachment[] => {
    return state.draftListing.localization?.de?.attachments || [];
  },
  getDraftListing: (state: ReferenceListingsState): Listing => state.draftListing,
  getCategories: (state: ReferenceListingsState): Category[] => {
    return state.draftListing.categories || [];
  },
  getNumberOfRooms: (state: ReferenceListingsState): number | undefined => {
    return state.draftListing.characteristics?.numberOfRooms || undefined;
  },
  getListingZipCode: (state: ReferenceListingsState): string => {
    return state.draftListing.address?.postalCode || '';
  },
  getListingCity: (state: ReferenceListingsState): string | undefined => {
    return state.draftListing.address?.locality ?? undefined;
  },
  getLivingSpace: (state: ReferenceListingsState): number | undefined => {
    return state.draftListing.characteristics?.livingSpace ?? undefined;
  },
  getAgent: (state: ReferenceListingsState): AgencyAgentType | undefined => {
    return state.draftListing.agencyAgent ?? undefined;
  },
  getPrice: (state: ReferenceListingsState): number | undefined => {
    return state.draftListing.prices?.buy?.price ?? undefined;
  },
  getListings: (state: ReferenceListingsState): ReferenceListing[] => state.listings,
  getApiGetCallStatus: (state: ReferenceListingsState): string => state.apiGetCallStatus,
  getApiDeleteCallStatus: (state: ReferenceListingsState): string | undefined =>
    state.apiDeleteCallStatus,
  getApiPutCallStatus: (state: ReferenceListingsState): string => state.apiPutCallStatus,
  getListingById:
    (state: ReferenceListingsState) =>
    (id: string): ReferenceListing | undefined => {
      return state.listings.find((listing) => listing.id === id);
    },
};

const actions = {
  updateDraft(
    { commit }: ActionContext<ReferenceListingsState, any>,
    updates: Partial<Listing>,
  ): void {
    commit('updateDraftListing', updates);
  },
  async fetchListings(
    context: ActionContext<ReferenceListingsState, any>,
    payload: { customerId: string },
  ): Promise<void> {
    context.commit('setApiGetCallStatus', ApiCallStatus.IN_PROGRESS);
    try {
      const listings = await getReferenceListings(payload.customerId);
      context.commit('setListings', listings);
      context.commit('setApiGetCallStatus', ApiCallStatus.SUCCESS);
    } catch (error) {
      context.commit('setApiGetCallStatus', ApiCallStatus.ERROR);
      console.error('Failed to fetch listings:', error);
    }
  },
  async deleteListing(
    context: ActionContext<ReferenceListingsState, any>,
    listingId: string,
  ): Promise<void> {
    context.commit('setApiDeleteCallStatus', ApiCallStatus.IN_PROGRESS);
    try {
      await deleteReferenceListingById(listingId);
      context.commit('setApiDeleteCallStatus', ApiCallStatus.SUCCESS);
    } catch (error) {
      context.commit('setApiDeleteCallStatus', ApiCallStatus.ERROR);
      throw error;
    }
  },
  async saveReferenceListing(
    context: ActionContext<ReferenceListingsState, any>,
    payload: { listing: ReferenceListing },
  ): Promise<void> {
    context.commit('setApiPutCallStatus', ApiCallStatus.IN_PROGRESS);
    try {
      // Transform payload for Commercial category: use totalFloorSpace instead of livingSpace
      const listingToSave = { ...payload.listing };
      const isCommercial = listingToSave.categories?.includes(Category.Commercial);
      const livingSpaceValue = listingToSave.characteristics?.livingSpace;
      const characteristics = listingToSave.characteristics;

      if (isCommercial && livingSpaceValue !== undefined && characteristics) {
        // Remove livingSpace and set totalFloorSpace
        const { livingSpace: _, ...characteristicsWithoutLivingSpace } =
          characteristics as Characteristics & { livingSpace?: number };
        listingToSave.characteristics = {
          ...characteristicsWithoutLivingSpace,
          totalFloorSpace: livingSpaceValue,
        };
      }
      await putReferenceListing(listingToSave);
      context.commit('setApiPutCallStatus', ApiCallStatus.SUCCESS);
    } catch (error) {
      console.error('Failed to save reference listing:', error);
      context.commit('setApiPutCallStatus', ApiCallStatus.ERROR);
    }
  },
};

const _default = {
  namespaced: true,
  state: getInitialState,
  getters,
  mutations,
  actions,
};
export default _default;
